/**
 * This module provides a `SearchSort` component for rendering a sortable search UI element
 * in a Sitecore and Algolia-based application. It includes helper components and utilities
 * for managing sort options and rendering the UI based on the current search context.
 *
 * The main exported component, `SearchSort`, dynamically adjusts its behavior based on
 * whether the application is in page editing mode or not. It uses Algolia's `SortBy` component
 * to provide sorting functionality for search results.
 *
 * Components and utilities included:
 * - `SearchSortWrapper`: A wrapper component for styling and layout of the sort UI.
 * - `SearchSortWithSearch`: A component that renders the sort UI with dynamic sort options
 *   based on the current search type.
 * - `StableSortItems`, `StableDefaultIndexes`, `StableEmployeeIndexes`: Predefined sets of
 *   sort items and indexes for different search contexts.
 *
 * Dependencies:
 * - `react-instantsearch-hooks-web` for Algolia search integration.
 * - `@sitecore-jss/sitecore-jss-nextjs` for Sitecore context.
 * - `tailwindcss-classnames` for styling.
 * - `lib/env` for environment-specific configurations.
 *
 * Usage:
 * - Import and use the `SearchSort` component in a page or layout to provide sorting
 *   functionality for search results.
 */
import { env } from 'lib/env';
import { classnames } from 'tailwindcss-classnames';
import { SortBy, useInstantSearch } from 'react-instantsearch-hooks-web';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSearchDictionary } from './util';
import { SearchWrapperProps } from './SearchTypes';
import { useCallback } from 'react';

/**
 * Contains a stabilized set of sort items and arrays of indexes for each supported search type.
 *
 * - `StableSortItems`: An array of sort options with labels and corresponding index values.
 *   These options include sorting by relevancy, date, first name (ascending/descending),
 *   and last name (ascending/descending).
 *
 * - `StableDefaultIndexes`: An array of default index values used for general search contexts.
 *   Includes the main index and a date-sorted index.
 *
 * - `StableEmployeeIndexes`: An array of index values specifically for employee-related searches.
 *   Includes indexes sorted by first name (ascending/descending) and last name (ascending/descending).
 *
 * - `StableDictionaryMap`: A mapping of index values to their corresponding dictionary keys.
 *
 * These constants are dynamically generated based on the default search index configured
 * in the environment variable (`env.ALGOLIA_INDEX_NAME`).
 */
const { StableSortItems, StableDefaultIndexes, StableEmployeeIndexes, StableDictionaryMap } =
  (() => {
    const DEFAULT_SEARCH_INDEX = env.ALGOLIA_INDEX_NAME;
    const stableSortItems = [
      { label: 'Relevancy', value: `${DEFAULT_SEARCH_INDEX}` },
      { label: 'Date', value: `${DEFAULT_SEARCH_INDEX}_date_desc` },
      { label: 'First Name Ascending', value: `${DEFAULT_SEARCH_INDEX}_firstname_asc` },
      { label: 'First Name Descending', value: `${DEFAULT_SEARCH_INDEX}_firstname_desc` },
      { label: 'Last Name Ascending', value: `${DEFAULT_SEARCH_INDEX}_lastname_asc` },
      { label: 'Last Name Descending', value: `${DEFAULT_SEARCH_INDEX}_lastname_desc` },
    ];
    const stableEmployeeIndexes = [
      `${DEFAULT_SEARCH_INDEX}_firstname_asc`,
      `${DEFAULT_SEARCH_INDEX}_firstname_desc`,
      `${DEFAULT_SEARCH_INDEX}_lastname_asc`,
      `${DEFAULT_SEARCH_INDEX}_lastname_desc`,
    ];
    const stableDefaultIndexes = [`${DEFAULT_SEARCH_INDEX}`, `${DEFAULT_SEARCH_INDEX}_date_desc`];
    const stableDictionaryMap: Record<string, keyof ReturnType<typeof useSearchDictionary>> = {
      [DEFAULT_SEARCH_INDEX]: 'sortRelevancy',
      [`${DEFAULT_SEARCH_INDEX}_date_desc`]: 'sortDate',
      [`${DEFAULT_SEARCH_INDEX}_firstname_asc`]: 'sortFirstNameAsc',
      [`${DEFAULT_SEARCH_INDEX}_firstname_desc`]: 'sortFirstNameDesc',
      [`${DEFAULT_SEARCH_INDEX}_lastname_asc`]: 'sortLastNameAsc',
      [`${DEFAULT_SEARCH_INDEX}_lastname_desc`]: 'sortLastNameDesc',
    };
    return {
      StableSortItems: stableSortItems,
      StableEmployeeIndexes: stableEmployeeIndexes,
      StableDefaultIndexes: stableDefaultIndexes,
      StableDictionaryMap: stableDictionaryMap,
    };
  })();

/**
 * A wrapper component for rendering a sortable search UI element.
 * It displays a label and wraps its children with styling for layout and alignment.
 *
 * @param {SearchWrapperProps & { sortLabel: string }} props - The props for the component.
 * @param {React.ReactNode} props.children - The child components to be rendered inside the wrapper.
 * @param {string} props.sortLabel - The label text to display for the sort functionality.
 *
 * @returns {JSX.Element} A styled wrapper containing the sort label and children.
 */
const SearchSortWrapper = ({
  children,
  sortLabel,
}: SearchWrapperProps & { sortLabel: string }): JSX.Element => {
  return (
    <div className={classnames('flex', 'items-center', 'mb-4', 'sm:mb-2')}>
      <span
        className={classnames(
          'text-blue-navy',
          'text-body',
          'font-semibold',
          'mr-6',
          'min-w-[50px]',
          'lg:min-w-0'
        )}
      >
        {sortLabel}
      </span>
      {children}
    </div>
  );
};

/**
 * A component that renders the search sorting UI with dynamic sort options
 * based on the current search type. It uses Algolia's `SortBy` component
 * to provide sorting functionality for search results.
 *
 * The sort options are dynamically adjusted depending on whether the search
 * type is "employee" or not. For "employee" searches, additional sort options
 * such as sorting by first name and last name (ascending/descending) are included.
 *
 * @returns {JSX.Element} The search sorting UI element wrapped in a `SearchSortWrapper`.
 */
const SearchSortWithSearch = () => {
  const { indexUiState: { menu: { type: selectedType } = {} } = {} } = useInstantSearch();
  const dict = useSearchDictionary();

  /**
   * Transforms an array of items by filtering and mapping them based on the selected type
   * and a dictionary mapping.
   *
   * @param items - An array of objects containing `label` and `value` properties.
   * @returns A new array of objects with transformed `label` and `value` properties.
   *
   * The transformation process:
   * - Filters the items based on whether their `value` exists in a predefined list
   *   (`StableEmployeeIndexes` or `StableDefaultIndexes`) determined by the `selectedType`.
   * - Maps the filtered items to a new structure where the `label` is derived from
   *   a dictionary (`StableDictionaryMap`) using the `value` as a key.
   *
   * Dependencies:
   * - `selectedType`: Determines which list of values to filter against.
   * - `dict`: A dictionary used to map `value` to a `label` via `StableDictionaryMap`.
   */
  const transformItems = useCallback(
    (items: Array<{ label: string; value: string }>) => {
      const lookIn = selectedType === 'employeee' ? StableEmployeeIndexes : StableDefaultIndexes;
      return items.reduce((acc, item) => {
        if (lookIn.includes(item.value)) {
          const labelKey = StableDictionaryMap[item.value];
          acc.push({ label: dict[labelKey], value: item.value });
        }
        return acc;
      }, [] as Array<{ label: string; value: string }>);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedType]
  );

  return (
    <SearchSortWrapper sortLabel={dict.sort}>
      <SortBy
        classNames={{
          root: classnames('w-full'),
          select: classnames('border', 'border-gray-spunPearl', 'rounded-md', 'p-2', 'w-full'),
        }}
        items={StableSortItems}
        transformItems={transformItems}
      />
    </SearchSortWrapper>
  );
};

/**
 * The main `SearchSort` component that conditionally renders either the
 * `SearchSortWrapper` or `SearchSortWithSearch` component based on the
 * current Sitecore context (page editing mode).
 *
 * If the application is in page editing mode, it renders the `SearchSortWrapper`
 * with a static sort label. Otherwise, it renders the `SearchSortWithSearch`
 * component to provide dynamic sorting functionality for search results.
 *
 * @returns {JSX.Element} The appropriate search sorting UI element based on the context.
 */
const SearchSort = () => {
  const { sitecoreContext } = useSitecoreContext();
  const pageEditing = sitecoreContext.pageEditing === true;
  if (pageEditing) {
    const theSortLabel = 'Search Sort';
    return <SearchSortWrapper sortLabel={theSortLabel} />;
  }
  return <SearchSortWithSearch />;
};

export default SearchSort;
